import { AxiosInstance as axios } from '../services/responseHandler'

import type { AxiosResponse } from 'axios'
import type { RuntimeConfig } from 'nuxt/schema'
import type { FeatureCollection, LineString } from 'geojson'
import type { SessionData } from '~/stores/auth'
import type { GetawayData } from '~/types/getaways'
import type { GeoSuggestionData } from '~/composables/useGeoService'
import type {
  GroupMember,
  GroupData,
  GroupLeaderboardEntry,
  GroupStatus,
  GroupPostData
} from '~/types/groups'
import type {
  MainSectionPhoto,
  TripPrivacy,
  TripData,
  ShareTripDataWithCoordinates
} from '~/types/trip'
import type { PaginationResponse } from '~/types/api'
import type { SeasonGoalEntry, UserProfile, UserProfileInfo } from '~/types/user'
import type { Posting, PostingComment } from '~/types/postings'
import type { GroupCommentsPage, GroupPosting } from '~/types/groups'
import type { SavedRoute } from '~/types/route'
import type { Notification } from '~/types/notifications'
import type { CountryRanking, Ranking, UserStats } from '~/types/statistics'
import type { Challenge, ChallengePreview } from '~/types/challenge'
import type { DistanceUnit } from '~/types/types'
import type {
  AdditionalBikePhoto,
  BikeData,
  BikeUpsertData,
  HelmetData,
  HelmetUpsertData,
  IntercomData,
  IntercomUpsertData,
  BikeService,
  BikeServiceUpsertData
} from '~/types/bikes'
import type { FeedResponse } from '~/types/feed'
import type { SectionData } from '~/types/sections'
import type { StandardLikeObjectType } from '~/types/like'
import type {
  GeoCodeResponse,
  GeoLocationInfo,
  GeoSuggestion,
  GeoAutocompleteResult
} from '~/types/geolocation'
import type { AchievementCollection, UserAchievement } from '~/types/achievements'
import type { SsoCheckResponse } from '~/types/auth'

interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  url: string
  data?: any
  apiUrl?: string
  apiPath?: string
  appAuth?: boolean
  silent?: boolean
  authorize?: boolean
  timeout?: number
  bypassProxy?: boolean
}

// Opt-in for endpoints called from pages that render without authentication.
export interface AppAuthOptions {
  forceAppAuth?: boolean
}

let config: RuntimeConfig
let token: string | null = null
let refreshToken: string | null = null

export const setAPIToken = (value: string | null) => {
  token = value
}

export const setRefreshToken = (value: string | null) => {
  refreshToken = value
}

export const setRuntimeConfig = (c: RuntimeConfig) => {
  config = c
}

export const retryRequest = async (config: any) => {
  const headers: Record<string, any> = {}

  if (token) {
    headers['Authorization'] = 'Token token=' + token
  }

  return await axios({
    ...config,
    headers
  })
}

const apiRequest = async <T = any>({
  method = 'GET',
  url,
  apiUrl = config.public.apiUrl,
  apiPath = 'api/v2/',
  data,
  appAuth = false,
  silent = false,
  authorize = true,
  timeout,
  bypassProxy = false
}: ApiRequestOptions): Promise<AxiosResponse<T | undefined | null>> => {
  const headers: Record<string, string> = {
    'riser-client-platform': 'web',
    'riser-client-version': '1.0.0'
  }

  if (authorize && token) {
    headers['Authorization'] = 'Token token=' + token
  }

  const proxiedBaseURL = import.meta.env.SSR ? config.public.serverAPIURL : apiUrl

  // Endpoints that can outlive Netlify's proxy timeout must talk to the API
  // origin directly (requires CORS on the backend). Falls back to the proxied
  // URL when no direct URL is configured, e.g. in local dev where apiUrl is
  // already an absolute, non-proxied host.
  const baseURL =
    bypassProxy && config.public.directApiUrl ? config.public.directApiUrl : proxiedBaseURL

  // Prepare parameters for axios request
  let parameters = {
    method,
    url: baseURL + apiPath + url,
    data,
    headers,
    params: method === 'GET' ? data : {},
    silent,
    timeout
  }

  // Make sure url has a trailing slash
  if (parameters.url.substr(-1) !== '/' && !parameters.url.includes('?')) {
    parameters.url += '/'
  }

  if (appAuth) {
    // Add app credentials if necessary
    parameters = {
      ...parameters,
      ...{
        auth: {
          username: config.public.apiUsername,
          password: config.public.apiPassword
        },
        withCredentials: true
      }
    }
  }

  const response = await axios(parameters)

  return response
}

export const API = {
  refreshSession: async (locale: string) => {
    if (!refreshToken) {
      throw new Error('No refresh token set')
    }

    const response = await apiRequest({
      method: 'POST',
      url: 'sessions',
      data: { refresh_token: refreshToken, locale },
      appAuth: true
    })
    return response
  },

  loginWithEmail: async (email: string, password: string, locale: string) => {
    return await apiRequest<SessionData>({
      method: 'POST',
      url: 'sessions/',
      data: { email, password, locale },
      appAuth: true
    })
  },

  verifyAppleUser: async (idToken: string) => {
    return await apiRequest<SsoCheckResponse>({
      method: 'POST',
      url: 'sso/user_exists',
      data: { apple_id_token: idToken }
    })
  },

  verifyFacebookUser: async (idToken: string) => {
    return await apiRequest<SsoCheckResponse>({
      method: 'POST',
      url: 'sso/user_exists',
      data: { fbtoken: idToken }
    })
  },

  verifyGoogleUser: async (idToken: string) => {
    return await apiRequest<SsoCheckResponse>({
      method: 'POST',
      url: 'sso/user_exists',
      data: { google_auth_token: idToken }
    })
  },

  createSessionWithGoogleToken: async (
    token: string,
    locale: string,
    email?: string,
    firstname?: string,
    lastname?: string,
    referrer?: string
  ) => {
    return await apiRequest<SessionData>({
      method: 'POST',
      url: 'sessions',
      data: {
        google_auth_token: token,
        email: email,
        firstname: firstname,
        lastname: lastname,
        locale,
        referrer
      },
      appAuth: true
    })
  },

  createSessionWithAppleToken: async (
    token: string,
    locale: string,
    email?: string,
    firstname?: string,
    lastname?: string,
    referrer?: string
  ) => {
    return await apiRequest<SessionData>({
      method: 'POST',
      url: 'sessions',
      appAuth: true,
      data: {
        apple_id_token: token,
        email: email,
        firstname: firstname,
        lastname: lastname,
        locale,
        referrer
      }
    })
  },

  createSessionWithFacebookToken: async (
    token: string,
    locale: string,
    email?: string,
    firstname?: string,
    lastname?: string,
    referrer?: string
  ) => {
    return await apiRequest<SessionData>({
      method: 'POST',
      url: 'sessions',
      appAuth: true,
      data: {
        fbtoken: token,
        email: email,
        firstname: firstname,
        lastname: lastname,
        locale,
        referrer
      }
    })
  },

  signup: async (
    email: string,
    password: string,
    firstname: string,
    lastname: string,
    locale: string,
    newsletter: boolean,
    referrer?: string
  ) => {
    return await apiRequest<UserProfile>({
      method: 'POST',
      url: 'users',
      data: {
        email,
        password,
        firstname,
        lastname,
        locale,
        newsletter,
        referrer
      },
      appAuth: true
    })
  },

  verifyUniqueEmail: async (email: string) => {
    return await apiRequest<void>({
      method: 'GET',
      url: 'users/unique/',
      data: { email },
      appAuth: true,
      silent: true
    })
  },

  verifyEmail: async (userID: string, data: { email_token: string }) => {
    return await apiRequest({
      method: 'POST',
      url: `users/${userID}/verify_email/`,
      data,
      appAuth: true
    })
  },

  getUserProfileInfo: async (userId: number) => {
    return await apiRequest<UserProfileInfo>({
      method: 'GET',
      url: `/users/${userId}/profile`
    })
  },

  getUserStats: async (userID: number, from?: Date, to?: Date) => {
    return await apiRequest<UserStats>({
      method: 'GET',
      url: `/users/${userID}/stats`,
      data: {
        from: from?.toISOString(),
        to: to?.toISOString()
      }
    })
  },

  getUserCountryRanking: async (from?: Date, to?: Date) => {
    return await apiRequest<CountryRanking>({
      method: 'GET',
      url: `/country_rankings`,
      data: {
        from: from?.toISOString(),
        to: to?.toISOString()
      }
    })
  },

  getUserFollowedRanking: async (from?: Date, to?: Date) => {
    return await apiRequest<Ranking>({
      method: 'GET',
      url: `/followed_rankings`,
      data: {
        from: from?.toISOString(),
        to: to?.toISOString()
      }
    })
  },

  getUserTrips: async (id: number | string, privacy?: TripPrivacy) => {
    return await apiRequest<TripData[]>({
      method: 'GET',
      url: 'users/' + id + '/trips',
      data: { privacy }
    })
  },

  getFeed: async (
    userID: number,
    params?: { after?: string; limit?: number; latitude?: number; longitude?: number }
  ) => {
    return await apiRequest<FeedResponse>({
      method: 'GET',
      url: 'users/' + userID + '/feed',
      data: params
    })
  },

  getFeedTrips: async (
    userID: number,
    params?: { after?: string; limit?: number; latitude?: number; longitude?: number }
  ) => {
    return await apiRequest<FeedResponse>({
      method: 'GET',
      url: 'users/' + userID + '/feed/trips',
      data: params
    })
  },

  getFeedPostings: async (
    userID: number,
    params?: { after?: string; limit?: number; latitude?: number; longitude?: number }
  ) => {
    return await apiRequest<FeedResponse>({
      method: 'GET',
      url: 'users/' + userID + '/feed/postings',
      data: params
    })
  },

  getFeedGarage: async (
    userID: number,
    params?: { after?: string; limit?: number; latitude?: number; longitude?: number }
  ) => {
    return await apiRequest<FeedResponse>({
      method: 'GET',
      url: 'users/' + userID + '/feed/garage',
      data: params
    })
  },

  getFeedJournal: async (
    userID: number,
    params?: { after?: string; limit?: number }
  ) => {
    return await apiRequest<FeedResponse>({
      method: 'GET',
      url: 'users/' + userID + '/feed/journal',
      data: params
    })
  },

  getUserProfileFeed: async (
    userId: number,
    params?: { after?: string; limit?: number }
  ) => {
    return await apiRequest<FeedResponse>({
      method: 'GET',
      url: 'users/' + userId + '/profile_feed',
      data: params
    })
  },

  fetchSuggestions: async <T>(
    fullUrl: string,
    params?: { after?: string; limit?: number }
  ) => {
    return await apiRequest<PaginationResponse<T>>({
      method: 'GET',
      url: fullUrl,
      apiUrl: '',
      apiPath: '',
      data: params
    })
  },

  getUserBikes: async (id: number) => {
    return apiRequest<BikeData[]>({
      method: 'GET',
      url: 'users/' + id + '/bikes',
      appAuth: token === null
    })
  },

  getUserHelmets: async (id: number) => {
    return await apiRequest<HelmetData[]>({
      method: 'GET',
      url: 'users/' + id + '/helmets'
    })
  },

  getUserIntercoms: async (id: number) => {
    return await apiRequest<IntercomData[]>({
      method: 'GET',
      url: 'users/' + id + '/intercoms'
    })
  },

  getUserNotifications: async (id: string) => {
    return await apiRequest<Notification[]>({
      method: 'GET',
      url: 'users/' + id + '/notifications',
      appAuth: token === null
    })
  },

  getUserSections: async (id: number) => {
    return await apiRequest<SectionData[]>({
      method: 'GET',
      url: 'users/' + id + '/sections',
      appAuth: token === null
    })
  },

  getUserGetaways: async (id: number) => {
    const tripResponse: any = await apiRequest({
      method: 'GET',
      url: 'users/' + id + '/getaways',
      appAuth: token === null
    })
    return tripResponse
  },

  getUserRoadbookSections: async (id: number) => {
    const tripResponse: any = await apiRequest({
      method: 'GET',
      url: 'users/' + id + '/roadbook_sections',
      appAuth: token === null
    })
    return tripResponse
  },

  getTrip: async (id: string) => {
    const tripResponse: any = await apiRequest({
      method: 'GET',
      url: 'trips/' + id,
      appAuth: token === null
    })
    return tripResponse
  },

  calculateTripRoute: async (id: number) => {
    return await apiRequest<{
      calculation_time: number
      code: string
      routes: any[]
      uuid: string
      waypoints: {
        name: string
        location: number[]
      }[]
    }>({
      method: 'POST',
      url: 'trips/' + id + '/route'
    })
  },

  createEvent: async (identifier: string | number, action: string, type: string) => {
    const response = await apiRequest({
      method: 'POST',
      url: 'events',
      data: {
        action,
        type,
        identifier
      }
    })
    return response
  },

  getPublicTrip: async (token: string) => {
    const tripResponse: any = await apiRequest({
      method: 'GET',
      apiPath: 'api/',
      url: 'sharing/trips/' + token + '/standard',
      appAuth: true
    })
    return tripResponse
  },

  getPublicTripWithCoordinates: async (token: string) => {
    return await apiRequest<ShareTripDataWithCoordinates>({
      method: 'GET',
      apiPath: 'api/',
      url: 'sharing/trips/' + token,
      appAuth: true
    })
  },

  getPublicTripPolyline: async (token: string) => {
    return await apiRequest<{ polyline: string }>({
      method: 'GET',
      apiPath: 'api/',
      url: 'sharing/trips/' + token + '/polyline',
      appAuth: true
    })
  },

  putTrip: async (
    id: string,
    data: {
      title: string
      note: string
      weather: any
      friend_ids: string[]
      hide_max_speed: boolean
      bike_id: string
      truncate_tails: boolean
      privacy: string
    }
  ): Promise<TripData> => {
    const tripResponse: any = await apiRequest({ method: 'PUT', url: 'trips/' + id, data })
    return tripResponse.data
  },

  putProfilePhoto: async (userID: string, data: FormData) => {
    return await apiRequest({
      method: 'POST',
      url: 'users/' + userID + '/profile_photo',
      data
    })
  },

  putCoverPhoto: async (userID: string, data: FormData) => {
    return await apiRequest({
      method: 'POST',
      url: 'users/' + userID + '/cover_photo',
      data
    })
  },

  putTripPhotos: async (data: FormData) => {
    return await apiRequest({
      method: 'POST',
      url: '/sections/photos',
      data
    })
  },

  deleteTrip: async (id: string) => {
    return await apiRequest({
      method: 'DELETE',
      url: '/trips/' + id
    })
  },

  deletePhoto: async (id: string | number) => {
    return await apiRequest<void>({
      method: 'DELETE',
      url: '/photos/' + id
    })
  },

  createAIStylizePhoto: async (photoId: number, unit: 'metric' | 'imperial') => {
    return await apiRequest<MainSectionPhoto>({
      method: 'POST',
      url: `photos/${photoId}/ai_stylize`,
      data: { unit },
      timeout: 300000,
      bypassProxy: true
    })
  },

  createPosting: async (post: { note: string; trip_id: number | null; files: File[] | null }) => {
    const data = new FormData()

    data.append('note', post.note)

    if (post.files && post.files.length > 0) {
      data.append('files', post.files[0])
    }

    return await apiRequest<Posting>({
      url: '/postings/',
      method: 'POST',
      data
    })
  },

  getPosting: async (id: string) => {
    return await apiRequest({
      url: `/postings/${id}/`,
      method: 'GET'
    })
  },

  putPosting: async ({ note, id }: { note: string; id: string }) => {
    await apiRequest({
      url: `/postings/${id}`,
      method: 'PUT',
      data: { note }
    })
  },

  deletePosting: async ({ id }: { id: string | number }) => {
    return await apiRequest({
      url: `/postings/${id}`,
      method: 'DELETE'
    })
  },

  reportPosting: async ({ id, note, reason }: { id: string; note: string; reason: string }) => {
    return await apiRequest({
      url: `/postings/${id}/complaints/`,
      method: 'POST',
      data: { note, reason }
    })
  },

  addPostingPhoto: async ({ data, id }: { data: FormData; id: string }) => {
    return await apiRequest({
      url: `/postings/${id}/photos/`,
      method: 'POST',
      data
    })
  },

  deletePostingPhotos: async ({ photoID, id }: { photoID: string; id: string }) => {
    return await apiRequest({
      url: `/postings/${id}/photos/${photoID}`,
      method: 'DELETE'
    })
  },

  createComment: async (endpoint: string, data: { text: string; timestamp: Date }) => {
    return await apiRequest({
      method: 'POST',
      url: endpoint,
      data
    })
  },

  deleteComment: async (endpoint: string) => {
    return await apiRequest({
      method: 'DELETE',
      url: endpoint
    })
  },

  putComment: async (endpoint: string, data: { text: string; timestamp: Date }) => {
    return await apiRequest({
      method: 'PUT',
      url: endpoint,
      data
    })
  },

  getComments: async (endpoint: string) => {
    return await apiRequest<PostingComment[]>({ method: 'GET', url: endpoint })
  },

  getCommentsPage: async (fullUrl: string) => {
    return await apiRequest<GroupCommentsPage>({
      url: fullUrl,
      apiUrl: '',
      apiPath: ''
    })
  },

  reportComment: async (endpoint: string, note: string, reason: string) => {
    return await apiRequest<PostingComment>({
      method: 'POST',
      url: endpoint,
      data: {
        note,
        reason
      }
    })
  },

  likeBike: async (id: number) => {
    return await apiRequest<BikeData>({
      method: 'POST',
      url: `/bikes/${id}/likes`
    })
  },

  unlikeBike: async (id: number) => {
    return await apiRequest<BikeData>({
      method: 'DELETE',
      url: `/bikes/${id}/likes`
    })
  },

  likeHelmet: async (id: number) => {
    return await apiRequest<HelmetData>({
      method: 'POST',
      url: `/helmets/${id}/likes`
    })
  },

  unlikeHelmet: async (id: number) => {
    return await apiRequest<HelmetData>({
      method: 'DELETE',
      url: `/helmets/${id}/likes`
    })
  },

  likeIntercom: async (id: number) => {
    return await apiRequest<IntercomData>({
      method: 'POST',
      url: `/intercoms/${id}/likes`
    })
  },

  unlikeIntercom: async (id: number) => {
    return await apiRequest<IntercomData>({
      method: 'DELETE',
      url: `/intercoms/${id}/likes`
    })
  },

  createLike: async (endpoint: string) => {
    return await apiRequest({
      method: 'POST',
      url: endpoint,
      data: { timestamp: new Date() }
    })
  },

  deleteLike: async (endpoint: string) => {
    return await apiRequest({
      method: 'DELETE',
      url: endpoint
    })
  },

  getLikes: async (
    contentType: StandardLikeObjectType,
    id: number,
    limit?: number,
    after?: string
  ) => {
    return await apiRequest<PaginationResponse<UserProfile>>({
      method: 'GET',
      url: `/${contentType}/${id}/likes`,
      data: { limit, after }
    })
  },

  createSeasonGoal: async (data: { start_date: Date; end_date: Date; distance_goal: number }) => {
    return await apiRequest({
      method: 'POST',
      url: '/season_goals/',
      data
    })
  },

  putSeasonGoal: async (
    goaldID: number,
    data: { start_date: Date; end_date: Date; distance_goal: number }
  ) => {
    return await apiRequest({
      method: 'PUT',
      url: `/season_goals/${goaldID}`,
      data
    })
  },

  getSeasonGoal: async () => {
    return await apiRequest<SeasonGoalEntry[]>({
      method: 'GET',
      url: '/season_goals/'
    })
  },

  deleteSeasonGoal: async (goaldID: number) => {
    return await apiRequest({
      method: 'DELETE',
      url: `/season_goals/${goaldID}`
    })
  },

  getRoutes: async (userID: number) => {
    return await apiRequest<SavedRoute[]>({
      method: 'GET',
      url: `/users/${userID}/saved_routes/?includeData=true`
    })
  },

  getRoute: async (userID: number, routeID: string | number) => {
    return await apiRequest<SavedRoute>({
      method: 'GET',
      url: `/users/${userID}/saved_routes/${routeID}`
    })
  },

  createRoute: async (
    userID: number,
    title: string,
    distance: number,
    duration: number,
    waypoints: { position: number[]; name?: string }[],
    weighting: string,
    tags: [],
    exclude: string[]
  ) => {
    return await apiRequest<SavedRoute>({
      method: 'POST',
      url: `users/${userID}/saved_routes`,
      data: {
        title,
        data: {
          distance,
          duration,
          waypoints,
          weighting,
          tags: [],
          exclude
        }
      }
    })
  },

  putRoute: async (
    userId: number,
    routeId: string,
    title: string,
    distance: number,
    duration: number,
    waypoints: { position: number[]; name?: string }[],
    weighting: string,
    tags: [],
    exclude: string[]
  ) => {
    return await apiRequest<SavedRoute>({
      method: 'PUT',
      url: `users/${userId}/saved_routes/${routeId}`,
      data: {
        title,
        data: {
          distance,
          duration,
          waypoints,
          weighting,
          tags: [],
          exclude
        }
      }
    })
  },

  deleteRoute: async (userId: number, routeId: string) => {
    return await apiRequest<void>({
      method: 'DELETE',
      url: 'users/' + userId + '/saved_routes/' + routeId
    })
  },

  getPolylineFromGPX: async (file: File) => {
    const formData = new FormData()

    formData.append('gpx_file', file)

    return await apiRequest<{
      polyline: string
    }>({
      method: 'POST',
      url: 'gpx/parse',
      data: formData
    })
  },

  getWaypointsFromPolyline: async (polyline: string, tolerance?: number) => {
    const data: {
      polyline: string
      tolerance?: number
    } = {
      polyline
    }

    if (tolerance) {
      data.tolerance = tolerance
    }

    return await apiRequest<{
      waypoints: [number, number][]
    }>({
      method: 'POST',
      url: 'gpx/waypoints',
      data
    })
  },

  getSection: async (id: number) => {
    return await apiRequest<SectionData>({
      method: 'GET',
      url: `sections/${id}/`,
      appAuth: token === null
    })
  },

  getSectionLocations: async (id: number) => {
    return await apiRequest<
      {
        longitude: number
        latitude: number
        altitude: number
        timestamp: string
        speed: number
      }[]
    >({
      method: 'GET',
      url: `sections/${id}/full_locations`,
      appAuth: token === null
    })
  },

  getSectionGeoJSON: async (id: string) => {
    return await apiRequest<FeatureCollection<LineString>>({
      method: 'GET',
      url: `sections/${id}/geojson`,
      appAuth: token === null
    })
  },

  getSectionPolyline: async (id: number) => {
    return await apiRequest<{
      polyline: string
    }>({
      method: 'GET',
      url: `sections/${id}/polyline`,
      appAuth: token === null
    })
  },

  getSectionWeather: async (id: string) => {
    return await apiRequest({
      method: 'GET',
      url: '/sections/' + id + '/weather',
      appAuth: token === null
    })
  },

  getUser: async (id: string | number, { forceAppAuth = false }: AppAuthOptions = {}) => {
    // Public pages must force app basic-auth rather than infer it from token
    // presence: a *stale* token still gets sent, 401s, and trips the global
    // handler in app.vue, which logs the visitor out and clears their cookies
    // as a side effect of opening a share link.
    const useAppAuth = forceAppAuth || token === null

    return await apiRequest<UserProfile>({
      method: 'GET',
      url: 'users/' + id,
      appAuth: useAppAuth,
      authorize: !useAppAuth
    })
  },

  deleteUser: async (id: string) => {
    return await apiRequest({
      method: 'DELETE',
      url: '/users/' + id
    })
  },

  putUser: async (
    id: string | number,
    data: {
      note?: string
      youtube_url?: string
      facebook_url?: string
      instagram_url?: string
      website_url?: string
      homebase?: string
      ai_image_consent?: boolean
      unit?: DistanceUnit
    }
  ) => {
    return await apiRequest({
      method: 'PUT',
      url: '/users/' + id,
      data
    })
  },

  getSuggestedFollows: async (userId: number) => {
    return await apiRequest<UserProfile[]>({
      url: 'users/' + userId + '/suggested_follows'
    })
  },

  getFollowers: async (userID: number, searchTerm?: string, limit?: number, after?: string) => {
    return await apiRequest<PaginationResponse<UserProfile>>({
      url: `users/${userID}/followers`,
      data: { limit, after, q: searchTerm }
    })
  },

  getFollowed: async (userID: number, searchTerm?: string, limit?: number, after?: string) => {
    return await apiRequest<PaginationResponse<UserProfile>>({
      url: `users/${userID}/followed`,
      data: { limit, after, q: searchTerm }
    })
  },

  getFollowRequests: async (
    userID: number,
    searchTerm?: string,
    limit?: number,
    after?: string
  ) => {
    return await apiRequest<PaginationResponse<UserProfile>>({
      url: `users/${userID}/incoming_follow_requests`,
      data: { limit, after, q: searchTerm }
    })
  },

  getBlockedUsers: async (userID: number, searchTerm?: string, limit?: number, after?: string) => {
    return await apiRequest<PaginationResponse<UserProfile>>({
      url: `users/${userID}/blocked_users`,
      data: { limit, after, q: searchTerm }
    })
  },

  acceptFollowRequest: async (userId: number) => {
    return await apiRequest<UserProfile>({
      method: 'POST',
      url: `users/${userId}/accept_follow`
    })
  },

  rejectFollowRequest: async (userId: number) => {
    return await apiRequest<UserProfile>({
      method: 'POST',
      url: `users/${userId}/remove_follow`
    })
  },

  follow: async (userId: number) => {
    return await apiRequest<UserProfile>({
      method: 'POST',
      url: `users/${userId}/follow`
    })
  },

  unFollow: async (userId: number) => {
    return await apiRequest<UserProfile>({
      method: 'POST',
      url: `users/${userId}/unfollow`
    })
  },

  removeFollower: async (followerID: number) => {
    return await apiRequest<UserProfile>({
      method: 'POST',
      url: `users/${followerID}/remove_follow`
    })
  },

  blockUser: async (userId: number) => {
    return await apiRequest<UserProfile>({
      method: 'POST',
      url: `users/${userId}/block`
    })
  },

  unBlockUser: async (userId: number) => {
    return await apiRequest<UserProfile>({
      method: 'POST',
      url: `users/${userId}/unblock`
    })
  },

  getIntercom: async (intercomId: number) => {
    return await apiRequest<IntercomData>({
      method: 'GET',
      url: 'intercoms/' + intercomId
    })
  },

  getHelmet: async (helmetId: number) => {
    return await apiRequest<HelmetData>({
      method: 'GET',
      url: 'helmets/' + helmetId
    })
  },

  createHelmet: async (userId: number, data: HelmetUpsertData) => {
    return await apiRequest<HelmetData>({
      method: 'POST',
      url: `users/${userId}/helmets/`,
      data: {
        ...data,
        user_id: userId
      }
    })
  },

  updateHelmet: async (helmetId: number, data: HelmetUpsertData) => {
    return await apiRequest<HelmetData>({
      method: 'PUT',
      url: `helmets/${helmetId}`,
      data
    })
  },

  createIntercom: async (userId: number, data: IntercomUpsertData) => {
    return await apiRequest<IntercomData>({
      method: 'POST',
      url: `users/${userId}/intercoms/`,
      data
    })
  },

  updateIntercom: async (intercomId: number, data: IntercomUpsertData) => {
    return await apiRequest<IntercomData>({
      method: 'PUT',
      url: `intercoms/${intercomId}`,
      data
    })
  },

  getBike: async (bikeID: number) => {
    return await apiRequest<BikeData>({
      method: 'GET',
      url: 'bikes/' + bikeID
    })
  },

  createBike: async (userId: number, data: BikeUpsertData) => {
    return await apiRequest<BikeData>({
      method: 'POST',
      url: 'users/' + userId + '/bikes',
      data
    })
  },

  updateBike: async (bikeId: number, data: BikeUpsertData) => {
    return await apiRequest<BikeData>({
      method: 'PUT',
      url: `/bikes/${bikeId}`,
      data
    })
  },

  updateBikePhoto: async (bikeId: number, photo: File) => {
    const formData = new FormData()

    formData.append('file', photo)

    return await apiRequest<BikeData>({
      method: 'POST',
      url: `/bikes/${bikeId}/photo`,
      data: formData
    })
  },

  addAdditionalBikePhoto: async (bikeId: number, photo: File) => {
    const formData = new FormData()

    formData.append('file', photo)

    return await apiRequest<AdditionalBikePhoto>({
      method: 'POST',
      url: `/bikes/${bikeId}/additional_photos`,
      data: formData
    })
  },

  updateHelmetPhoto: async (helmetId: number, photo: File) => {
    const formData = new FormData()
    formData.append('file', photo)

    return await apiRequest<HelmetData>({
      method: 'POST',
      url: `/helmets/${helmetId}/photo`,
      data: formData
    })
  },

  updateIntercomPhoto: async (intercomId: number, photo: File) => {
    const formData = new FormData()
    formData.append('file', photo)

    return await apiRequest<IntercomData>({
      method: 'POST',
      url: `/intercoms/${intercomId}/photo`,
      data: formData
    })
  },

  deleteBike: async (bikeId: number) => {
    return await apiRequest<void>({
      method: 'DELETE',
      url: `/bikes/${bikeId}`
    })
  },

  deleteHelmet: async (helmetId: number) => {
    return await apiRequest<void>({
      method: 'DELETE',
      url: `/helmets/${helmetId}`
    })
  },

  deleteIntercom: async (intercomId: number) => {
    return await apiRequest<void>({
      method: 'DELETE',
      url: `/intercoms/${intercomId}`
    })
  },

  getHelmetBrands: async () => {
    return await apiRequest<{ id: number; name: string }[]>({
      method: 'GET',
      url: 'helmets/brands',
      appAuth: true
    })
  },

  getIntercomBrands: async () => {
    return await apiRequest<
      {
        id: number
        name: string
        models: {
          id: number
          name: string
        }[]
      }[]
    >({
      method: 'GET',
      url: 'intercoms/brands',
      appAuth: true
    })
  },

  getBikeBrands: async () => {
    return await apiRequest<
      {
        id: number
        name: string
        suggested_by: number | null
        models: {
          id: number
          name: string
          suggested_by: number | null
        }[]
      }[]
    >({
      method: 'GET',
      url: 'bikes/brands',
      appAuth: true
    })
  },

  getGroup: async (groupID: string | number, fetchAnonymously: boolean = false) => {
    return await apiRequest<GroupData>({
      method: 'GET',
      url: `groups/${groupID}`,
      authorize: !fetchAnonymously
    })
  },

  createGroup: async (userID: string | number, data: GroupPostData) => {
    return await apiRequest<GroupData>({
      method: 'POST',
      url: `users/${userID}/groups`,
      data
    })
  },

  putGroup: async (groupID: string | number, data: GroupPostData) => {
    return await apiRequest<GroupData>({
      method: 'PUT',
      url: `groups/${groupID}`,
      data
    })
  },

  deleteGroup: async (groupID: string | number) => {
    return await apiRequest({
      method: 'DELETE',
      url: `groups/${groupID}`
    })
  },

  updateGroupCoverImage: async (id: string | number, data: FormData) => {
    return await apiRequest({
      method: 'POST',
      url: 'groups/' + id + '/cover_photo',
      data
    })
  },

  updateGroupProfileImage: async (id: string | number, data: FormData) => {
    return await apiRequest({
      method: 'POST',
      url: 'groups/' + id + '/profile_photo',
      data
    })
  },

  getGroupStatus: async (groupID: string | number, userID: string | number) => {
    return await apiRequest<{
      group_id: number
      status: GroupStatus
    }>({
      method: 'GET',
      url: `groups/${groupID}/status/${userID}`
    })
  },

  createGroupPosting: async (
    groupID: number,
    post: { note: string; files: File[]; tripIds: number[] }
  ) => {
    const data = new FormData()

    data.append('note', post.note)

    if (post.files.length > 0) {
      data.append('files', post.files[0])
    }

    if (post.tripIds.length > 0) {
      data.append('trips', post.tripIds[0].toString())
    }

    return await apiRequest({
      method: 'POST',
      url: `groups/${groupID}/postings`,
      data
    })
  },

  deleteGroupPosting: async (groupId: number, postingId: number) => {
    return await apiRequest({
      method: 'DELETE',
      url: `groups/${groupId}/postings/${postingId}`
    })
  },

  pinGroupPosting: async (groupId: number, postingId: number) => {
    return await apiRequest({
      method: 'POST',
      url: `groups/${groupId}/postings/${postingId}/pin`
    })
  },

  unpinGroupPosting: async (groupId: number, postingId: number) => {
    return await apiRequest({
      method: 'POST',
      url: `groups/${groupId}/postings/${postingId}/unpin`
    })
  },

  reportGroupPosting: async ({
    groupId,
    postingId,
    reason,
    note
  }: {
    groupId: number
    postingId: number
    reason: string
    note: string
  }) => {
    return await apiRequest({
      method: 'POST',
      url: `groups/${groupId}/postings/${postingId}/complaints`,
      data: { reason, note }
    })
  },

  getGroupPosting: async (groupId: number, postingId: number) => {
    return await apiRequest<GroupPosting>({
      url: `groups/${groupId}/postings/${postingId}`,
      method: 'GET'
    })
  },

  getGroupPostings: async (groupID: number, limit?: number, after?: string) => {
    return await apiRequest<PaginationResponse<GroupPosting>>({
      url: `groups/${groupID}/postings`,
      data: { limit, after }
    })
  },

  likeGroupPosting: async (groupId: number, postingId: number) => {
    return await apiRequest({
      method: 'POST',
      url: `/groups/${groupId}/postings/${postingId}/likes`
    })
  },

  unlikeGroupPosting: async (groupId: number, postingId: number) => {
    return await apiRequest({
      method: 'DELETE',
      url: `/groups/${groupId}/postings/${postingId}/likes`
    })
  },

  getGroupPostingLikes: async (
    groupId: number,
    postingId: number,
    limit?: number,
    after?: string
  ) => {
    return await apiRequest<PaginationResponse<UserProfile>>({
      method: 'GET',
      url: `groups/${groupId}/postings/${postingId}/likes`,
      data: { limit, after }
    })
  },

  getYearlyGroupLeaderboard: async (groupID: number) => {
    return await apiRequest<GroupLeaderboardEntry[]>({
      method: 'GET',
      url: `groups/${groupID}/leaderboard/year`
    })
  },

  getMonthlyGroupLeaderboard: async (groupID: number) => {
    return await apiRequest<GroupLeaderboardEntry[]>({
      method: 'GET',
      url: `groups/${groupID}/leaderboard/month`
    })
  },

  getAllTimeGroupLeaderboard: async (groupID: number) => {
    return await apiRequest<GroupLeaderboardEntry[]>({
      method: 'GET',
      url: `groups/${groupID}/leaderboard/alltime`
    })
  },

  getGroupInvitees: async (groupID: string | number) => {
    return await apiRequest<GroupMember[]>({
      method: 'GET',
      url: `groups/${groupID}/invited`
    })
  },

  getGroupMembers: async (groupID: string | number, limit?: number, after?: string) => {
    return await apiRequest<PaginationResponse<GroupMember>>({
      method: 'GET',
      url: `groups/${groupID}/members`,
      data: { limit, after }
    })
  },

  getGroupRequests: async (groupID: string | number) => {
    return await apiRequest<GroupMember[]>({
      method: 'GET',
      url: `groups/${groupID}/requested`
    })
  },

  getInviteeCandidates: async (
    groupId: string | number,
    limit?: number,
    after?: string,
    q?: string
  ) => {
    return await apiRequest<PaginationResponse<UserProfile>>({
      method: 'GET',
      url: `groups/${groupId}/invitee_candidates`,
      data: { limit, after, q }
    })
  },

  inviteToGroup: async (groupID: string | number, data: { user_ids: (string | number)[] }) => {
    return await apiRequest<GroupMember[]>({
      method: 'POST',
      url: 'groups/' + groupID + '/invite/',
      data
    })
  },

  cancelGroupInvite: async (groupID: number, userID: number) => {
    return await apiRequest({
      method: 'DELETE',
      url: 'groups/' + groupID + '/invite/' + userID
    })
  },

  getUserGroups: async (userID: string | number) => {
    return await apiRequest<GroupData[]>({
      method: 'GET',
      url: 'users/' + userID + '/groups/'
    })
  },

  getUserGroupRequests: async (userID: string | number) => {
    return await apiRequest({
      method: 'GET',
      url: 'users/' + userID + '/group_requests/'
    })
  },

  getUserGroupInvites: async (userID: string | number, limit?: number, after?: string) => {
    return await apiRequest<PaginationResponse<GroupData>>({
      method: 'GET',
      url: 'users/' + userID + '/group_invitations/',
      data: { limit, after }
    })
  },

  acceptGroupInvite: async (groupID: string | number) => {
    return await apiRequest({
      method: 'POST',
      url: 'groups/' + groupID + '/accept_invite/'
    })
  },

  acceptGroupJoinRequest: async (groupID: string | number, userIDs: (string | number)[]) => {
    return await apiRequest({
      method: 'POST',
      url: 'groups/' + groupID + '/accept_requests/',
      data: {
        user_ids: userIDs
      }
    })
  },

  createGroupRequest: async (groupID: string | number) => {
    return await apiRequest({
      method: 'POST',
      url: 'groups/' + groupID + '/request/'
    })
  },

  deleteGroupRequest: async (groupID: string | number, userID: string | number) => {
    return await apiRequest({
      method: 'DELETE',
      url: 'groups/' + groupID + '/members/' + userID
    })
  },

  getGetaway: async (id: string) => {
    return await apiRequest({
      method: 'GET',
      url: `/getaways/${id}`
    })
  },

  getGetawayInvitations: async (id: string) => {
    return await apiRequest({
      method: 'GET',
      url: `getaways/${id}/getaway_invitations`
    })
  },

  deleteGetaway: async (id: number) => {
    return await apiRequest({
      method: 'DELETE',
      url: `/getaways/${id}`
    })
  },

  putGetaway: async (id: number, data: Partial<GetawayData>) => {
    return await apiRequest({
      method: 'PUT',
      url: `/getaways/${id}`,
      data
    })
  },

  createGetawayInvitation: async (id: string | number, data: { user_ids: (string | number)[] }) => {
    return await apiRequest<GetawayData>({
      method: 'POST',
      url: `/getaways/${id}/getaway_invitations/`,
      data
    })
  },

  putGetawayInvitation: async (id: string, data: { status: string }) => {
    return await apiRequest({
      method: 'PUT',
      url: `getaway_invitations/${id}`,
      data
    })
  },

  deleteGetawayInvitation: async (id: string) => {
    return await apiRequest({
      method: 'DELETE',
      url: `getaway_invitations/${id}`
    })
  },

  createGetaway: async (userID: number, data: Partial<GetawayData>) => {
    return await apiRequest({
      method: 'POST',
      url: `users/${userID}/getaways/`,
      data
    })
  },

  putGetawayPhoto: async (id: number, data: FormData) => {
    return await apiRequest({
      method: 'POST',
      url: `/getaways/${id}/photo`,
      data
    })
  },

  getUsers: async (query: string) => {
    return await apiRequest({
      url: '/users/search',
      data: { q: query }
    })
  },

  getUserAchievements: async (userID: string) => {
    return await apiRequest<UserAchievement[]>({
      url: `users/${userID}/achievements/`
    })
  },

  getAchievements: async () => {
    return await apiRequest<{
      collections: AchievementCollection[]
    }>({
      url: '/achievements/',
      appAuth: true
    })
  },

  getGroups: async (query: string, limit?: number, after?: string) => {
    return await apiRequest<PaginationResponse<GroupData>>({
      url: '/groups/',
      data: { q: query, limit, after }
    })
  },

  discoverGroups: async (lat: number, long: number, limit?: number, after?: string) => {
    return await apiRequest<PaginationResponse<GroupData>>({
      url: '/groups/discover',
      data: { latitude: lat, longitude: long, limit, after }
    })
  },

  getChallenge: async (id: string) => {
    return await apiRequest<Challenge>({
      method: 'GET',
      url: `challenges/${id}/`,
      appAuth: !token
    })
  },

  joinChallenge: async (id: string | number) => {
    return await apiRequest<{
      id: number
      user_id: number
      challenge_id: number
      created_at: string
      updated_at: string
    }>({
      method: 'POST',
      url: `challenges/${id}/join/`,
      appAuth: !token
    })
  },

  leaveChallenge: async (id: string) => {
    return await apiRequest({
      method: 'POST',
      url: `challenges/${id}/leave/`,
      appAuth: !token
    })
  },

  getCustomChallenge: async (id: string) => {
    return await apiRequest<Challenge>({
      method: 'GET',
      url: `custom_challenges/${id}/`,
      appAuth: !token
    })
  },

  joinCustomChallenge: async (id: string) => {
    return await apiRequest({
      method: 'POST',
      url: `custom_challenges/${id}/join/`,
      appAuth: !token
    })
  },

  leaveCustomChallenge: async (id: string) => {
    return await apiRequest({
      method: 'POST',
      url: `custom_challenges/${id}/leave/`,
      appAuth: !token
    })
  },

  getChallenges: async (isoCode: string) => {
    return await apiRequest<ChallengePreview[]>({
      method: 'GET',
      url: '/challenges/',
      appAuth: !token,
      data: {
        country: isoCode
      }
    })
  },

  geoCode: async (gid: string, lang?: string) => {
    return await apiRequest<GeoCodeResponse>({
      url: 'geo/geocode/',
      appAuth: !token,
      data: {
        magicKey: gid,
        lang: lang
      }
    })
  },

  reverseGeoCode: async (data: GeoSuggestionData) => {
    return await apiRequest<
      [
        {
          geometry: {
            coordinates: [number, number]
          }
          properties: {
            name: string
          }
        }
      ]
    >({
      url: 'geo/reverse',
      appAuth: !token,
      data
    })
  },

  getGeoSuggestions: async (data: GeoSuggestionData) => {
    return await apiRequest<GeoSuggestion[]>({
      url: 'geo/suggest',
      appAuth: !token,
      data
    })
  },

  autocompletePlaces: async (data: { text: string; lang?: string; lat?: number; lon?: number }) => {
    return await apiRequest<GeoAutocompleteResult>({
      url: 'geo/autocomplete',
      appAuth: !token,
      data
    })
  },

  searchPlaces: async (data: { text: string; lang?: string; lat?: number; lon?: number }) => {
    return await apiRequest<GeoAutocompleteResult>({
      url: 'geo/search',
      appAuth: !token,
      data
    })
  },

  getWeather: async (latitude: number, longitude: number) => {
    return await apiRequest({
      method: 'GET',
      url: '/weather',
      data: { latitude, longitude }
    })
  },

  requestPasswordReset: async (email: string) => {
    return await apiRequest({
      method: 'POST',
      url: 'reset_password_email/',
      data: { email }
    })
  },

  setPassword: async (password: string, token: string) => {
    return await apiRequest({
      method: 'POST',
      url: 'set_password',
      data: { token, password }
    })
  },

  consumeVoucher: async (voucher: string, referrer?: string) => {
    return await apiRequest<{
      pro_days: number
      redeemed: boolean
      revenuecat_offer_id: string | null
      stripe_price_id: string | null
    }>({
      method: 'POST',
      url: 'vouchers/use/',
      data: { voucher, referrer }
    })
  },

  getTermsOfService: async (locale: string) => {
    return await apiRequest({
      method: 'GET',
      url: 'tos_object/?language=' + locale
    })
  },

  acceptTermsOfService: async () => {
    return await apiRequest({ url: 'accept_tos', apiPath: '' })
  },

  getLocation: async () => {
    return await apiRequest<GeoLocationInfo>({ url: 'geoip' })
  },

  createStripeCheckoutSession: async (
    stripePriceId: string,
    voucher?: string,
    stripePromoCode?: string,
    referralId?: string,
    skipTrial: boolean = false
  ) => {
    return await apiRequest<{ client_secret: string }>({
      method: 'POST',
      url: 'stripe/create_checkout_session',
      data: {
        stripe_promotion_code: stripePromoCode,
        price_id: stripePriceId,
        voucher,
        skip_trial: skipTrial,
        referral: referralId
      }
    })
  },

  createStripeBillingPortalSession: async () => {
    return await apiRequest<{ url: string }>({
      method: 'POST',
      url: 'stripe/create_billing_portal_session'
    })
  },

  getBikeServices: async (bikeId: number) => {
    return await apiRequest<BikeService[]>({
      method: 'GET',
      url: `bikes/${bikeId}/services`
    })
  },

  getBikeService: async (bikeId: number, serviceId: number) => {
    return await apiRequest<BikeService>({
      method: 'GET',
      url: `bikes/${bikeId}/services/${serviceId}`
    })
  },

  createBikeService: async (bikeId: number, data: BikeServiceUpsertData) => {
    return await apiRequest<BikeService>({
      method: 'POST',
      url: `bikes/${bikeId}/services`,
      data
    })
  },

  updateBikeService: async (bikeId: number, serviceId: number, data: BikeServiceUpsertData) => {
    return await apiRequest<BikeService>({
      method: 'PUT',
      url: `bikes/${bikeId}/services/${serviceId}`,
      data
    })
  },

  deleteBikeService: async (bikeId: number, serviceId: number) => {
    return await apiRequest({
      method: 'DELETE',
      url: `bikes/${bikeId}/services/${serviceId}`
    })
  },

  createRewindVideo: async (
    tripId: number,
    options: {
      unit?: 'metric' | 'imperial'
      lineColor?: string
      showPhotos?: boolean
      showTitle?: boolean
      title?: string
      orientation?: 'portrait' | 'landscape'
    }
  ) => {
    return await apiRequest({
      method: 'POST',
      url: `trips/${tripId}/rewind`,
      data: {
        unit: options.unit,
        line_color: options.lineColor,
        show_photos: options.showPhotos,
        show_title: options.showTitle,
        title: options.title,
        orientation: options.orientation
      }
    })
  }
}

export type RISERAPI = typeof API
